import { createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect, type JSX } from "react";

import { graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";

import { AppointmentTypeForm } from "@/components/appointment/type/appointment-type-form";
import { AppointmentTypeFormLegacy } from "@/components/appointment/type/appointment-type-form-legacy";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { PendingComponent } from "@/components/ui/loading/pending-component";
import { useGraphQL } from "@/hooks/use-graphql";
import { appointmentTypeFlowExitIndexAtom } from "@/lib/atoms/appointment-type-flow-atom";

const GetAppointmentTypeCreatePageQuery = graphql(`
  query GetAppointmentTypeCreatePageQuery(
    $codUsuario: Int!
    $codUsuarioCompromisso: Int!
  ) {
    ...GetAppointmentTypeFormQueryFragment
    ...GetAppointmentTypeFormLegacyQueryFragment
  }
`);

const CreateAppointmentTypePage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const { flags } = useRouteContext({ from: "__root__" });

  const showImprovedAppointmentType = flags["ativa-melhorias-tipo-de-atendimento"];

  const router = useRouter();
  const setFlowExitIndex = useSetAtom(appointmentTypeFlowExitIndexAtom);

  useEffect(() => {
    setFlowExitIndex(router.history.location.state.__TSR_index - 1);
  }, [router, setFlowExitIndex]);

  const queryResult = useGraphQL(GetAppointmentTypeCreatePageQuery, {
    codUsuarioCompromisso: 0,
    codUsuario: Number(user.codUsuario),
  });

  const { data } = queryResult;

  const content = (
    <Page className={showImprovedAppointmentType ? "pb-0" : undefined}>
      <AsyncDataWrapper {...queryResult}>
        {data && queryResult.isFetchedAfterMount ? (
          showImprovedAppointmentType ? (
            <AppointmentTypeForm data={data} />
          ) : (
            <AppointmentTypeFormLegacy data={data} />
          )
        ) : (
          <FullPageSpinner />
        )}
      </AsyncDataWrapper>
    </Page>
  );

  if (!showImprovedAppointmentType) {
    return (
      <>
        <HeaderRoot>
          <HeaderButton icon={Left} align="start" />
          <HeaderTitle title="Criar tipo de atendimento" align="center" />
        </HeaderRoot>
        {content}
      </>
    );
  }

  return (
    <RoundedLayout className="-mb-[env(safe-area-inset-bottom)]">
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.Title
            title="Criar tipo de atendimento"
            description="Essas configurações ajudam pacientes a agendar sozinhos pelo site ou por telefone."
          />
        </FlowHeader.Actions>
      </FlowHeader>
      {content}
    </RoundedLayout>
  );
};

export const Route = createFileRoute(
  "/settings/schedule-options/appointment-types/create/",
)({
  component: CreateAppointmentTypePage,
  pendingComponent: () => <PendingComponent title="Criar tipo de atendimento" />,
});
